import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import changelogData from '@/data/changelog.json';
import './changelog.css';

interface Release {
  version: string;
  date: string;
  changes: string[];
}

/** Changelog dates in JSON (YYYY-MM-DD) are authored in US Eastern time. */
function formatChangelogDate(dateStr: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!match) return dateStr;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  const date = new Date(Date.UTC(year, month - 1, day, 12));
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/New_York'
  });
}

const ChangelogScreen: React.FC = () => {
  const { t } = useTranslation('screens');
  const { version } = useParams<{ version?: string }>();
  const releaseRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    if (version) {
      const element = releaseRefs.current.get(version);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [version]);

  return (
    <div className="changelog-page">
      <header className="changelog-page__header">
        <h1 className="changelog-page__title">{t('changelog.title')}</h1>
        <p className="changelog-page__lede">{t('changelog.lede')}</p>
      </header>

      <div className="changelog-page__releases">
        {changelogData.releases.map((release: Release) => (
          <article
            key={release.version}
            id={`v${release.version}`}
            ref={(el) => {
              if (el) releaseRefs.current.set(release.version, el);
            }}
            className={`changelog-page__release ${version === release.version ? 'changelog-page__release--highlighted' : ''}`}
          >
            <header className="changelog-page__release-header">
              <Link to={`/changelog/${release.version}`} className="changelog-page__version-link">
                <time className="changelog-page__date" dateTime={release.date}>
                  {formatChangelogDate(release.date)}
                </time>
                <span className="changelog-page__version">v{release.version}</span>
              </Link>
            </header>
            <ul className="changelog-page__changes">
              {release.changes.map((change, index) => (
                <li key={index} className="changelog-page__change">
                  {change}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </div>
  );
};

export default ChangelogScreen;
